import React, { createContext, useContext, useMemo } from 'react';

export const AppContext = createContext({});

/**
 * Make context available across all elements in the app.
 * This hook is works with Functional components.
 * @return {{}}
 */
export function useAppContext() {
  return useContext(AppContext);
}

/**
 * Create Provider to store a currency settings.
 *
 * @param {JSX.Element} children
 * @param {Object} context
 * @return {JSX.Element}
 * @constructor
 */
export function AppProvider({ children, context }) {
  let currencySettings;
  // Get settings from the window object or from the context.
  if (typeof window !== 'undefined') {
    currencySettings = window.__CURRENCY_SETTINGS__ || {};
  } else {
    currencySettings = context?.currencyConfiguration || {};
  }

  const contextValue = {
    currencySettings,
  };

  const settingsString = JSON.stringify(contextValue);

  // Memoize the settings, so that we do not cause unintended re-renders.
  // Use primitive type (strings settings) as dependency, when the object is
  // small enough! Otherwise, look for a different solution with dependencies.
  // Disabling react-hooks/exhaustive-deps, as we've provided a correct
  // dependency, in strings settings form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedContextValue = useMemo(() => contextValue, [settingsString]);

  return (
    <AppContext.Provider value={memoizedContextValue}>
      {children}
    </AppContext.Provider>
  );
}

export default AppContext;
